import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';
import type { AccountEntity } from '@actual-app/core/types/models';

import { isAccountFailedSync } from '#accounts/syncStatus';
import { Page } from '#components/Page';
import { useAccountBalances } from '#hooks/useAccountBalances';
import { useClosedAccounts } from '#hooks/useClosedAccounts';
import { useFailedAccounts } from '#hooks/useFailedAccounts';
import { useOffBudgetAccounts } from '#hooks/useOffBudgetAccounts';
import { useOnBudgetAccounts } from '#hooks/useOnBudgetAccounts';

import { cartao, cartaoDestaque, cor, tela, texto } from './estilo';

/** Branco esmaecido para o texto de apoio dentro do card verde. */
const tintaSobreVerde = 'rgba(255, 255, 255, 0.72)';
import { LinhaDeConta } from './patrimonio/LinhaDeConta';
import type { ContaNaTela } from './patrimonio/LinhaDeConta';

/**
 * Soma os saldos das linhas EXIBIDAS.
 *
 * ⚠️ `parcial` existe porque `useAccountBalances` devolve `null` enquanto o
 * spreadsheet ainda não reportou aquele saldo. Somar `null` como zero em
 * silêncio faria o total mentir por alguns instantes e o dono não teria como
 * saber — a tela diz que o número ainda está incompleto (ver
 * `data-testid="patrimonio-parcial"`).
 */
export function somarSaldos(linhas: ContaNaTela[]): {
  total: number;
  parcial: boolean;
} {
  let total = 0;
  let parcial = false;

  for (const linha of linhas) {
    if (linha.saldo === null) {
      parcial = true;
    } else {
      total += linha.saldo;
    }
  }

  return { total, parcial };
}

type PatrimonioVisaoProps = {
  /** As do dia a dia (`useOnBudgetAccounts`): corrente, cartão. */
  contas: ContaNaTela[];
  /** As de fora do orçamento (`useOffBudgetAccounts`). */
  guardado: ContaNaTela[];
  /** Fechadas (`useClosedAccounts`) — escondidas até ele pedir. */
  fechadas: ContaNaTela[];
};

/**
 * A metade PURA da tela: recebe as linhas prontas e não conhece hook nenhum.
 *
 * É ela que `Patrimonio.test.tsx` renderiza — o total, a divisão em grupos e
 * a exclusão das fechadas são decididos aqui, então o teste prova a regra de
 * verdade, sem precisar de spreadsheet, Redux ou servidor de mentira.
 */
export function PatrimonioVisao({
  contas,
  guardado,
  fechadas,
}: PatrimonioVisaoProps) {
  const { t } = useTranslation();
  const [mostrarFechadas, setMostrarFechadas] = useState(false);

  /**
   * ⛔ O total é SÓ das contas abertas — mesmo com "mostrar fechadas" ligado.
   * Uma conta fechada é dinheiro que não existe mais ali; somá-la inflaria o
   * patrimônio. Por isso o bloco das fechadas diz, na própria tela, que elas
   * não entram no total.
   */
  const { total, parcial } = somarSaldos([...contas, ...guardado]);

  const nenhumaConta =
    contas.length === 0 && guardado.length === 0 && fechadas.length === 0;

  return (
    <View style={tela}>
      {/* ⭐ O protagonista vem num card VERDE com texto branco — é assim que o
          MULTICAP destaca o número principal de cada tela (medido na tela
          "Patrimônio" deles): por inversão de cor, não só por tamanho. */}
      <View style={{ ...cartaoDestaque, gap: 8 }}>
        <Text style={{ ...texto.rotuloCartao, color: tintaSobreVerde }}>
          <Trans>Total nas suas contas</Trans>
        </Text>

        <Text
          data-testid="patrimonio-total"
          style={{ ...texto.valorGrande, color: '#ffffff' }}
        >
          {total < 0 ? '-' : ''}R$ {integerToCurrency(Math.abs(total))}
        </Text>

        {parcial && (
          <Text
            data-testid="patrimonio-parcial"
            style={{ ...texto.corpo, color: tintaSobreVerde }}
          >
            <Trans>
              Ainda estou lendo o saldo de algumas contas — este total vai
              ajustar.
            </Trans>
          </Text>
        )}
      </View>

      {nenhumaConta && (
        <View style={cartao}>
          <Text
            data-testid="patrimonio-vazio"
            style={{ ...texto.corpo, color: cor.tintaFraca }}
          >
            <Trans>
              Nenhuma conta cadastrada ainda. Assim que existir uma, o saldo
              dela aparece aqui.
            </Trans>
          </Text>
        </View>
      )}

      {contas.length > 0 && (
        <View data-testid="grupo-contas" style={{ ...cartao, gap: 4 }}>
          <Text style={texto.tituloBloco}>
            <Trans>Contas</Trans>
          </Text>
          {contas.map(conta => (
            <LinhaDeConta key={conta.id} conta={conta} />
          ))}
        </View>
      )}

      {guardado.length > 0 && (
        <View data-testid="grupo-guardado" style={{ ...cartao, gap: 4 }}>
          {/* ⚠️ "Guardado", nunca "fora do orçamento"/"off budget": é o mesmo
              dinheiro da coluna "Guardei" da tela Mês, e o jargão do Actual é
              metade da sensação de "não é intuitivo" (spec §6). */}
          <Text style={texto.tituloBloco}>
            <Trans>Guardado</Trans>
          </Text>
          {guardado.map(conta => (
            <LinhaDeConta key={conta.id} conta={conta} />
          ))}
        </View>
      )}

      {fechadas.length > 0 && (
        <View style={{ ...cartao, gap: 4 }}>
          <Button
            variant="bare"
            data-testid="patrimonio-alternar-fechadas"
            aria-expanded={mostrarFechadas}
            style={{ alignSelf: 'flex-start' }}
            onPress={() => setMostrarFechadas(atual => !atual)}
          >
            <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
              {mostrarFechadas
                ? t('esconder contas fechadas')
                : t('mostrar contas fechadas ({{quantas}})', {
                    quantas: fechadas.length,
                  })}
            </Text>
          </Button>

          {mostrarFechadas && (
            <View data-testid="grupo-fechadas" style={{ gap: 4 }}>
              {fechadas.map(conta => (
                <LinhaDeConta key={conta.id} conta={conta} />
              ))}
              <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
                <Trans>Conta fechada não entra no total acima.</Trans>
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

/**
 * "Patrimônio" (`/patrimonio`) — o destino da lista de contas que sai da
 * barra lateral do Actual (spec §6). Existe por uma razão só: sem ela, tirar
 * a lista da barra deixaria o dono sem caminho nenhum para as contas no
 * desktop — sem extrato, sem saldo por conta e sem lançar à mão. Cada linha
 * leva a `/accounts/:id`, que é onde essas três coisas acontecem.
 *
 * ⭐ Tudo sai de hook pronto do Actual — nenhuma tabela nova, nenhuma query
 * nova, nenhum arquivo deles tocado.
 *
 * ⛔ Fora deste mínimo, de propósito: gavetas customizáveis
 * (`account_groups`), gráfico de evolução, bens (carro/imóvel) e qualquer
 * edição de conta. É a rodada do "Patrimônio completo".
 */
export function Patrimonio() {
  const { t } = useTranslation();

  // `useOnBudgetAccounts`/`useOffBudgetAccounts` já excluem as fechadas
  // (`accounts/queries.ts` → `selectActive`), então nenhuma conta aparece em
  // dois grupos.
  const { data: contas = [] } = useOnBudgetAccounts();
  const { data: guardado = [] } = useOffBudgetAccounts();
  const { data: fechadas = [] } = useClosedAccounts();
  const falhas = useFailedAccounts();

  // Liga o saldo de TODAS, inclusive as fechadas: assim alternar "mostrar
  // fechadas" não dispara um religa-tudo de bindings (o hook re-liga sempre
  // que o CONJUNTO de ids muda).
  const saldos = useAccountBalances(
    [...contas, ...guardado, ...fechadas].map(conta => conta.id),
  );

  function paraTela(conta: AccountEntity): ContaNaTela {
    return {
      id: conta.id,
      nome: conta.name,
      saldo: saldos[conta.id] ?? null,
      // Duas origens de propósito: `useFailedAccounts` é a falha da sessão
      // atual (Redux, some ao recarregar a página) e `isAccountFailedSync`
      // é a que ficou gravada na conta (`bank_sync_status`). Só a primeira
      // deixaria um banco desconectado ontem invisível hoje.
      falhaDeSincronizacao: falhas.has(conta.id) || isAccountFailedSync(conta),
    };
  }

  return (
    <Page header={t('Patrimônio')}>
      <PatrimonioVisao
        contas={contas.map(paraTela)}
        guardado={guardado.map(paraTela)}
        fechadas={fechadas.map(paraTela)}
      />
    </Page>
  );
}
